/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Divider,
  LinearProgress,
  Alert,
  Badge,
  Chip,
  Autocomplete,
} from "@mui/material"
import { CameraAlt, Save, CloudOff, CloudDone, CheckCircle, Warning, Sync, Public, Info } from "@mui/icons-material"
import imageCompression from "browser-image-compression"
import localforage from "localforage"
import Swal from "sweetalert2"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Tema optimizado para exteriores y móviles (Alto contraste)
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#d32f2f" },
    success: { main: "#2E7D32" },
    warning: { main: "#ed6c02" },
  },
  typography: { button: { fontWeight: "bold", fontSize: "1.1rem" } },
})

const IngresoActaDelegado = () => {
  // ================= ESTADOS DEL FLUJO =================
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [juntas, setJuntas] = useState([])
  const [candidatos, setCandidatos] = useState([])
  const [dignidades, setDignidades] = useState([])
  const [actasPendientes, setActasPendientes] = useState(0)

  // Estados de Seguridad y RLS
  const [recintoActual, setRecintoActual] = useState("")
  const [esGlobal, setEsGlobal] = useState(false)

  // Formulario - Selección
  const [juntaSeleccionada, setJuntaSeleccionada] = useState(null)
  const [dignidadSeleccionada, setDignidadSeleccionada] = useState(null)
  const [padronMesa, setPadronMesa] = useState(0)

  // Formulario - Evidencia
  const [fotoActa, setFotoActa] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isCompressing, setIsCompressing] = useState(false)

  // Formulario - Digitación
  const [votosValidosManuales, setVotosValidosManuales] = useState("")
  const [votosBlancos, setVotosBlancos] = useState("")
  const [votosNulos, setVotosNulos] = useState("")
  const [votosCandidatos, setVotosCandidatos] = useState({})

  // ================= 1. INICIALIZACIÓN Y RED =================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarDatosBase()
    verificarColaLocal()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const verificarColaLocal = async () => {
    const cola = (await localforage.getItem("actas_offline_queue")) || []
    setActasPendientes(cola.length)
  }

  const cargarDatosBase = async () => {
    try {
      if (isOffline) throw new Error("Offline_Mode")

      const res = await api.post("/ControlElectoral/getDatosDelegado", {})
      const data = res?.data !== undefined ? res.data : res

      if (data && data.success) {
        const isGlobalBackend = data.es_global || false

        const juntasFormateadas = data.data.juntas.map((j) => {
          let nombreBase = `Junta ${j.junta_numero} - ${j.junta_genero === "M" ? "Masculino" : "Femenino"}`
          if (isGlobalBackend) {
            nombreBase += ` (Recinto: ${j.reci_codigo})`
          }
          return {
            id: j.junta_codigo,
            label: nombreBase,
            empadronados: j.junta_empadronados,
          }
        })

        const candidatosFrescos = data.data.candidatos || []
        const dignidadesFrescas = data.data.dignidades || []
        const recintoAsignado = data.recinto || ""

        setJuntas(juntasFormateadas)
        setCandidatos(candidatosFrescos)
        setDignidades(dignidadesFrescas)
        setRecintoActual(recintoAsignado)
        setEsGlobal(isGlobalBackend)

        await localforage.setItem("catalogo_juntas_cache", juntasFormateadas)
        await localforage.setItem("catalogo_candidatos_cache", candidatosFrescos)
        await localforage.setItem("catalogo_dignidades_cache", dignidadesFrescas)
        await localforage.setItem("actas_recinto_cache", recintoAsignado)
        await localforage.setItem("actas_global_cache", isGlobalBackend)
      } else {
        Swal.fire("Aviso", data?.message || "No tiene mesas asignadas.", "warning")
      }
    } catch (error) {
      console.log("Cargando catálogo desde la memoria caché...")

      const juntasCache = await localforage.getItem("catalogo_juntas_cache")
      const candidatosCache = await localforage.getItem("catalogo_candidatos_cache")
      const dignidadesCache = await localforage.getItem("catalogo_dignidades_cache")
      const rCache = (await localforage.getItem("actas_recinto_cache")) || ""
      const gCache = (await localforage.getItem("actas_global_cache")) || false

      if (juntasCache && candidatosCache && dignidadesCache) {
        setJuntas(juntasCache)
        setCandidatos(candidatosCache)
        setDignidades(dignidadesCache)
        setRecintoActual(rCache)
        setEsGlobal(gCache)
      } else {
        Swal.fire({
          icon: "error",
          title: "Error Crítico de Red",
          text: "No tiene conexión y aún no ha descargado su padrón de mesas.",
        })
      }
    }
  }

  // ================= 2. LÓGICA DE FOTOGRAFÍA =================
  const handleCapturePhoto = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    setIsCompressing(true)
    try {
      const options = { maxSizeMB: 0.4, maxWidthOrHeight: 1920, useWebWorker: true }
      const compressedFile = await imageCompression(file, options)

      const reader = new FileReader()
      reader.readAsDataURL(compressedFile)
      reader.onloadend = () => {
        setFotoActa(reader.result)
        setPreviewUrl(URL.createObjectURL(compressedFile))
        setIsCompressing(false)
      }
    } catch (error) {
      console.error("Error comprimiendo", error)
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo procesar la fotografía." })
      setIsCompressing(false)
    }
  }

  // ================= 3. VALIDACIÓN MATEMÁTICA EN VIVO =================
  const candidatosFiltrados = dignidadSeleccionada
    ? candidatos.filter((c) => String(c.dig_codigo) === String(dignidadSeleccionada.id))
    : []

  // Sumatorias
  const totalCandidatos = candidatosFiltrados.reduce(
    (acc, cand) => acc + (parseInt(votosCandidatos[cand.cand_codigo]) || 0),
    0,
  )
  const validosDigitados = parseInt(votosValidosManuales) || 0
  const blancosDigitados = parseInt(votosBlancos) || 0
  const nulosDigitados = parseInt(votosNulos) || 0

  // Total Sufragantes = Válidos Digitados + Blancos + Nulos
  const totalGeneral = validosDigitados + blancosDigitados + nulosDigitados

  const handleJuntaChange = (newValue) => {
    setJuntaSeleccionada(newValue)
    setPadronMesa(newValue ? newValue.empadronados : 0)
  }

  const handleVotoChange = (cand_codigo, valor) => {
    const soloNumeros = valor.replace(/\D/g, "")
    setVotosCandidatos((prev) => ({ ...prev, [cand_codigo]: soloNumeros }))
  }

  // ================= 4. ENVÍO Y ENCOLAMIENTO OFFLINE =================
  const handleSubmit = async () => {
    if (!juntaSeleccionada) return Swal.fire("Atención", "Seleccione la Junta", "warning")
    if (!dignidadSeleccionada) return Swal.fire("Atención", "Seleccione la Dignidad", "warning")
    if (!fotoActa) return Swal.fire("Atención", "Debe capturar la fotografía del acta firmada", "warning")
    if (totalGeneral === 0) return Swal.fire("Atención", "No ha ingresado ningún voto", "warning")

    // REGLA MATEMÁTICA 1: Votos Válidos vs Suma de Candidatos
    if (validosDigitados !== totalCandidatos) {
      return Swal.fire({
        icon: "error",
        title: "Inconsistencia de Votos Válidos",
        text: `Ha digitado ${validosDigitados} Votos Válidos, pero la suma de los candidatos da ${totalCandidatos}. Corrija para continuar.`,
      })
    }

    // REGLA MATEMÁTICA 2: Total General vs Padrón
    if (totalGeneral > padronMesa) {
      return Swal.fire({
        icon: "error",
        title: "¡Inconsistencia Numérica!",
        text: `Ha ingresado ${totalGeneral} votos en total, pero el padrón es de ${padronMesa} empadronados. No puede haber más votos que votantes.`,
      })
    }

    // Nota: Si totalGeneral < padronMesa, el sistema DEJA GUARDAR (es ausentismo válido).

    const payload = {
      junta_codigo: juntaSeleccionada.id,
      dig_codigo: dignidadSeleccionada.id,
      sufragantes: totalGeneral,
      validos_digitados: validosDigitados,
      blancos: blancosDigitados,
      nulos: nulosDigitados,
      votos: candidatosFiltrados.map((c) => ({
        cand_codigo: c.cand_codigo,
        cantidad: parseInt(votosCandidatos[c.cand_codigo]) || 0,
      })),
      foto_base64: fotoActa,
      timestamp: new Date().toISOString(),
    }

    if (isOffline) {
      const cola = (await localforage.getItem("actas_offline_queue")) || []
      cola.push(payload)
      await localforage.setItem("actas_offline_queue", cola)
      setActasPendientes(cola.length)

      Swal.fire({ icon: "info", title: "Guardado Localmente", text: "El acta se enviará cuando recupere la señal." })
      limpiarFormulario()
    } else {
      try {
        Swal.fire({ title: "Enviando...", allowOutsideClick: false, didOpen: () => Swal.showLoading() })
        // IMPORTANTE: Asegúrate de tener el prefijo /ControlElectoral
        const res = await api.post("/ControlElectoral/recepcion/acta", payload)
        const data = res?.data !== undefined ? res.data : res

        if (data.success) {
          Swal.fire({
            icon: "success",
            title: "¡Enviado!",
            text: "Acta recibida exitosamente en el Centro de Cómputo.",
          })
          limpiarFormulario()
        } else {
          throw new Error(data.message)
        }
      } catch (error) {
        const cola = (await localforage.getItem("actas_offline_queue")) || []
        cola.push(payload)
        await localforage.setItem("actas_offline_queue", cola)
        setActasPendientes(cola.length)
        Swal.fire({ icon: "warning", title: "Red inestable", text: "Fallo al enviar. Guardado localmente." })
        limpiarFormulario()
      }
    }
  }

  const limpiarFormulario = () => {
    setDignidadSeleccionada(null)
    setFotoActa(null)
    setPreviewUrl(null)
    setVotosValidosManuales("")
    setVotosBlancos("")
    setVotosNulos("")
    setVotosCandidatos({})
  }

  const sincronizarCola = async () => {
    if (isOffline) return Swal.fire("Aún sin conexión", "Busque un lugar con señal celular.", "warning")
    const cola = (await localforage.getItem("actas_offline_queue")) || []
    if (cola.length === 0) return Swal.fire("Todo al día", "No hay actas pendientes de envío.", "success")

    Swal.fire({
      title: "Sincronizando...",
      text: `Subiendo ${cola.length} actas`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    })

    let subidas = 0
    let colaRestante = [...cola]

    for (let i = 0; i < cola.length; i++) {
      try {
        await api.post("/ControlElectoral/recepcion/acta", cola[i])
        subidas++
        colaRestante = colaRestante.filter(
          (acta) =>
            !(acta.junta_codigo === cola[i].junta_codigo && String(acta.dig_codigo) === String(cola[i].dig_codigo)),
        )
      } catch (err) {
        console.error("Fallo al subir acta encolada", err)
      }
    }

    await localforage.setItem("actas_offline_queue", colaRestante)
    setActasPendientes(colaRestante.length)

    if (colaRestante.length === 0) {
      Swal.fire("¡Sincronización Completa!", `Se subieron ${subidas} actas correctamente.`, "success")
    } else {
      Swal.fire("Sincronización Parcial", `Se subieron ${subidas} actas. Quedan pendientes.`, "warning")
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-2" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <BackIcon />

        <Box sx={{ pb: 15 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            p={2}
            bgcolor="white"
            borderRadius={2}
            boxShadow={1}
          >
            <Box display="flex" alignItems="center">
              {isOffline ? (
                <CloudOff color="error" sx={{ fontSize: 32, mr: 1 }} />
              ) : (
                <CloudDone color="success" sx={{ fontSize: 32, mr: 1 }} />
              )}
              <Typography variant="subtitle1" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
                {isOffline ? "MODO OFFLINE" : "CONECTADO"}
              </Typography>
            </Box>
            <Badge badgeContent={actasPendientes} color="secondary">
              <IconButton
                onClick={sincronizarCola}
                disabled={actasPendientes === 0 || isOffline}
                sx={{ bgcolor: "#e3f2fd" }}
              >
                <Sync color={actasPendientes > 0 ? "primary" : "disabled"} />
              </IconButton>
            </Badge>
          </Box>

          {isOffline && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <b>SIN INTERNET:</b> Los datos se guardarán en su celular. <b>NO CIERRE EL NAVEGADOR.</b>
            </Alert>
          )}

          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, borderTop: "6px solid #196C87" }}>
            {recintoActual && (
              <Box display="flex" alignItems="center" mb={3} p={1.5} bgcolor="#e1f5fe" borderRadius={2}>
                <Typography
                  variant="subtitle2"
                  color="primary.dark"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  📍 Cobertura: {recintoActual}
                </Typography>
                {esGlobal && (
                  <Chip
                    icon={<Public fontSize="small" />}
                    label="MODO GLOBAL"
                    color="info"
                    size="small"
                    sx={{ ml: 2, fontWeight: "bold" }}
                  />
                )}
              </Box>
            )}

            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              1. Selección de Mesa y Dignidad
            </Typography>
            <Grid container spacing={2} mb={4}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={juntas}
                  getOptionLabel={(option) => option.label || ""}
                  value={juntaSeleccionada}
                  onChange={(event, newValue) => handleJuntaChange(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Busque o elija la Junta"
                      placeholder="Ej: Junta 1"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={dignidades}
                  getOptionLabel={(option) => option.label || ""}
                  value={dignidadSeleccionada}
                  onChange={(event, newValue) => setDignidadSeleccionada(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Dignidad del Acta (Ej. Alcalde)" variant="outlined" />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              2. Evidencia Física
            </Typography>
            <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
              <input
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                id="icon-button-file"
                type="file"
                onChange={handleCapturePhoto}
              />
              <label htmlFor="icon-button-file" style={{ width: "100%" }}>
                <Button
                  variant={fotoActa ? "outlined" : "contained"}
                  color="primary"
                  component="span"
                  fullWidth
                  size="large"
                  startIcon={<CameraAlt sx={{ fontSize: 28 }} />}
                  sx={{ py: 2, borderRadius: 2 }}
                >
                  {fotoActa ? "VOLVER A TOMAR FOTO" : "TOCAR PARA ABRIR CÁMARA"}
                </Button>
              </label>
              {isCompressing && <LinearProgress sx={{ width: "100%", mt: 2 }} />}
              {previewUrl && (
                <Box mt={2} textAlign="center">
                  <img
                    src={previewUrl}
                    alt="Acta"
                    style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "8px", border: "2px solid #196C87" }}
                  />
                  <Typography variant="caption" display="block" color="success.main" fontWeight="bold">
                    ✓ Imagen lista para enviar
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              3. Digitación de Votos
            </Typography>

            <Box bgcolor="#e8f5e9" p={2} borderRadius={2} mb={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="tel"
                    label="Votos VÁLIDOS (Digitado)"
                    value={votosValidosManuales}
                    onChange={(e) => setVotosValidosManuales(e.target.value.replace(/\D/g, ""))}
                    inputProps={{
                      style: {
                        fontSize: "1.5rem",
                        textAlign: "center",
                        color: validosDigitados !== totalCandidatos && validosDigitados > 0 ? "red" : "inherit",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    type="tel"
                    label="Votos BLANCOS"
                    value={votosBlancos}
                    onChange={(e) => setVotosBlancos(e.target.value.replace(/\D/g, ""))}
                    inputProps={{ style: { fontSize: "1.5rem", textAlign: "center" } }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    type="tel"
                    label="Votos NULOS"
                    value={votosNulos}
                    onChange={(e) => setVotosNulos(e.target.value.replace(/\D/g, ""))}
                    inputProps={{ style: { fontSize: "1.5rem", textAlign: "center" } }}
                  />
                </Grid>
              </Grid>

              {/* Alerta Matemática 1: Sumatoria de Candidatos vs Válidos Digitados */}
              {validosDigitados > 0 && validosDigitados !== totalCandidatos && (
                <Typography variant="caption" color="error" display="block" textAlign="center" mt={1} fontWeight="bold">
                  ⚠️ ALERTA: Válidos Digitados ({validosDigitados}) NO COINCIDEN con la suma de candidatos (
                  {totalCandidatos}).
                </Typography>
              )}
            </Box>

            {dignidadSeleccionada &&
              candidatosFiltrados.map((cand, idx) => (
                <Card
                  key={cand.cand_codigo}
                  elevation={1}
                  sx={{ mb: 2, borderLeft: idx === 0 ? "5px solid #196C87" : "5px solid #bdbdbd" }}
                >
                  <CardContent sx={{ p: "16px !important", display: "flex", alignItems: "center" }}>
                    <Box flexGrow={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {cand.cand_nombre}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {cand.cand_lista}
                      </Typography>
                    </Box>
                    <Box width="100px">
                      <TextField
                        type="tel"
                        fullWidth
                        placeholder="0"
                        value={votosCandidatos[cand.cand_codigo] || ""}
                        onChange={(e) => handleVotoChange(cand.cand_codigo, e.target.value)}
                        inputProps={{ style: { fontSize: "1.5rem", textAlign: "center", fontWeight: "bold" } }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}

            {!dignidadSeleccionada && (
              <Typography variant="body2" color="textSecondary" textAlign="center" my={4}>
                Seleccione una dignidad en el Paso 1 para habilitar la lista de candidatos.
              </Typography>
            )}

            <Paper
              elevation={4}
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                bgcolor: totalGeneral > padronMesa || validosDigitados !== totalCandidatos ? "#ffebee" : "#ffffff",
                borderTop: "2px solid #e0e0e0",
                zIndex: 1000,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} px={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Total Sufragantes:{" "}
                  <span style={{ color: totalGeneral > padronMesa ? "red" : "#196C87", fontSize: "1.3rem" }}>
                    {totalGeneral}
                  </span>
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Padrón: {padronMesa}
                </Typography>
              </Box>

              {/* Alerta Matemática 2: Ausentismo vs Inconsistencia */}
              {totalGeneral > padronMesa && padronMesa > 0 && (
                <Typography variant="caption" color="error" display="flex" alignItems="center" mb={1} fontWeight="bold">
                  <Warning fontSize="small" sx={{ mr: 0.5 }} /> Inconsistencia: Votos superan el padrón.
                </Typography>
              )}
              {totalGeneral < padronMesa && padronMesa > 0 && totalGeneral > 0 && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  display="flex"
                  alignItems="center"
                  mb={1}
                  fontWeight="bold"
                >
                  <Info fontSize="small" sx={{ mr: 0.5 }} /> Posible Ausentismo: Faltan {padronMesa - totalGeneral}{" "}
                  votantes.
                </Typography>
              )}

              <Button
                variant="contained"
                color={isOffline ? "secondary" : "primary"}
                fullWidth
                size="large"
                startIcon={isOffline ? <Save /> : <CheckCircle />}
                onClick={handleSubmit}
                sx={{ py: 1.5, fontSize: "1.2rem", borderRadius: 2 }}
              >
                {isOffline ? "GUARDAR EN CELULAR" : "ENVIAR AL CENTRO DE CÓMPUTO"}
              </Button>
            </Paper>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default IngresoActaDelegado
